import type { TareaEstadisticasResponse } from '@app/shared';

import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';

import { GraficoSemanal } from './GraficoSemanal';

interface Props {
  datos: TareaEstadisticasResponse;
}

// ciclo-tareas: por tarea Y por semana (pedido de Javier, 28/09/2026) — los
// totales + el gráfico de barras semana a semana arriba, el detalle por tarea
// (con "la que más cuesta") abajo.
export function Estadisticas({ datos }: Props): JSX.Element {
  const es = useI18n();
  const t = es.tareas.estadisticas;

  if (datos.semanas.length === 0) {
    return <p className="py-10 text-center text-sm text-text-subtle">{t.vacio}</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-3 gap-3">
        <Tarjeta titulo={t.totalTareas} valor={datos.totalTareas} />
        <Tarjeta titulo={t.totalSemanas} valor={datos.totalSemanasCerradas} />
        <Tarjeta titulo={t.cumplimientoGlobal} valor={`${String(datos.porcentajeGlobal)}%`} tono="success" />
      </div>

      <div className="rounded-xl border border-border-subtle bg-surface p-4 shadow-card">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-text-subtle">{t.graficoTitulo}</p>
        <GraficoSemanal semanas={datos.semanas} titulo={t.graficoTitulo} />
      </div>

      {/* D-044: qué se propuso cada semana y cómo le fue, de la más reciente a la más vieja. */}
      <div className="rounded-xl border border-border-subtle bg-surface p-4 shadow-card">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-text-subtle">
          {t.objetivosTitulo}
        </p>
        <ul className="flex flex-col divide-y divide-border-subtle">
          {datos.semanas
            .slice()
            .reverse()
            .map((s) => (
              <li
                key={`${String(s.anio)}-${String(s.numero)}`}
                data-testid="objetivo-historico"
                className="flex items-baseline gap-3 py-2"
              >
                <span className="w-10 shrink-0 text-xs text-text-faint">S{s.numero}</span>
                <span
                  className={cn('flex-1 text-sm', s.objetivo ? 'text-text-strong' : 'italic text-text-faint')}
                >
                  {s.objetivo ?? t.sinObjetivo}
                </span>
                <span className="shrink-0 text-sm font-semibold text-text-marca">
                  {String(s.porcentajeCumplimiento)}%
                </span>
              </li>
            ))}
        </ul>
      </div>

      <div className="flex flex-col gap-3">
        {datos.tareas.map((te) => (
          <div
            key={te.tareaId ?? te.titulo}
            className={cn(
              'rounded-xl border bg-surface p-4 shadow-card',
              te.tareaId && te.tareaId === datos.peorTareaId ? 'border-danger' : 'border-border-subtle',
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium text-text-strong">{te.titulo}</span>
              <span className="text-sm font-semibold text-text-marca">
                {t.cumplimiento.replace('{pct}', String(te.porcentajeCumplimiento))}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex gap-1">
                {te.ultimasSemanas
                  .slice()
                  .reverse()
                  .map((s) => (
                    <span
                      key={s.numero}
                      title={`Semana ${s.numero}`}
                      className={cn('h-2.5 w-2.5 rounded-full', s.cumplida ? 'bg-success' : 'bg-danger')}
                    />
                  ))}
              </div>
              <span className="text-xs text-text-faint">
                {t.semanasRegistradas.replace('{n}', String(te.totalSemanas))}
              </span>
            </div>
            {te.tareaId && te.tareaId === datos.peorTareaId && (
              <p className="mt-2 text-sm text-danger">{t.peorTarea.replace('{titulo}', te.titulo)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Tarjeta({
  titulo,
  valor,
  tono = 'neutro',
}: {
  titulo: string;
  valor: string | number;
  tono?: 'neutro' | 'success';
}): JSX.Element {
  return (
    <div className="rounded-md border border-marca-neutro bg-surface p-4 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">{titulo}</p>
      <p
        className={cn(
          'mt-1 text-3xl font-bold tabular-nums',
          tono === 'success' ? 'text-success' : 'text-text-strong',
        )}
      >
        {valor}
      </p>
    </div>
  );
}
